import {
  Component,
  h,
  Element,
  State,
  Method,
  Host,
  Watch,
} from "@stencil/core"
import {
  selectReducer,
  getInitialState,
  SelectState,
  SelectActions,
  isKeyboardBehavior,
} from "../select-store"
import {
  StoreConnection,
  StateHandle,
  computedProperty,
  getAppearance,
} from "../../../core/helpers/webcomponent-helpers"

/**
 * This Web Component serves only to allow to append the list of options to the
 * body in order to overlap every thing else (overlaying).
 */
@Component({
  tag: "sdx-select-list",
  styleUrl: "select-list.scss",
  shadow: true,
})
export class SelectList {
  private store: StoreConnection<this, SelectState, SelectActions>
  private hadPlaceholderWhenOpened?: boolean
  private filterInputEl?: HTMLSdxInputElement

  @Element() public el!: HTMLSdxSelectListElement

  @State() public state!: StateHandle<SelectState>

  @Watch("state")
  public stateChanged({}, prev: StateHandle<SelectState>) {
    const state = this.state.get()
    const prevState = prev.get()

    if (state.display !== prevState.display) {
      this.displayChanged(state)
    }
  }

  /**
   * @private
   */
  @Method()
  public async doFocus() {
    this.filterInputEl?.doFocus()
  }

  /**
   * @private
   */
  @Method()
  public async doBlur() {
    this.filterInputEl!.doBlur()
  }

  constructor() {
    this.store = new StoreConnection(this, selectReducer, getInitialState(), [
      "selection",
      "optionEls",
      "multiple",
      "directionY",
      "listEl",
      "keyboardBehavior",
      "filter",
      "filterInputElValue",
      "filterInputElPlaceholder",
      "filterMinLength",
      "noMatchesFound",
      "noMatchesFoundLabel",
      "placeholder",
      "required",
      "display",
      "loading",
      "isInsideDialog",
    ])
  }

  public connectedCallback() {
    // Register self
    this.store.set("listEl", this.el)
  }

  public componentDidLoad() {
    this.store.subscribe()
  }

  private displayChanged(state: SelectState) {
    if (state.display === "opening") {
      // Was the placeholder displayed when opening?
      this.hadPlaceholderWhenOpened = this.showPlaceholder()
    } else if (state.display === "closed") {
      this.hadPlaceholderWhenOpened = undefined
    }
  }

  /**
   * Determines whether the placeholder option should be rendered:
   *  - when no selection is in progress (UX: list should not grow while open),
   *  - when something is selected,
   *  - the placeholder prop exists
   *  - and when single select
   */
  private showPlaceholder(): boolean {
    const { selection, placeholder, multiple, required } = this.state.get()

    let showPlaceholder =
      !!selection.length && !!placeholder && !multiple && !required

    if (this.hadPlaceholderWhenOpened !== undefined) {
      showPlaceholder = this.hadPlaceholderWhenOpened
    }

    return showPlaceholder
  }

  private onFilterInput(e: Event): void {
    const value = (e.target as HTMLSdxInputElement).value

    this.store.set("filterInputElValue", value)
    this.store.dispatch({ type: "SET_FILTER", filter: value })
  }

  private getHostClassNames() {
    const { display, directionY, isInsideDialog } = this.state.get()

    return {
      [getAppearance(this.el)]: true,
      [display]: true,
      ...computedProperty(directionY),
      "inside-dialog": isInsideDialog,
    }
  }

  public render() {
    const state = this.state.get()

    return (
      <Host tabindex="-1" class={this.getHostClassNames()}>
        <div class="component">
          {isKeyboardBehavior(state, "filter") && (
            <div class="filter-input-wrapper">
              <sdx-input
                class="filter-input"
                value={state.filterInputElValue}
                placeholder={state.filterInputElPlaceholder}
                selectTextOnFocus={true}
                clearable={true}
                onInput={(e) => this.onFilterInput(e)}
                ref={(el) => (this.filterInputEl = el)}
              />
            </div>
          )}

          {this.showPlaceholder() && (
            <sdx-select-option placeholder={true}>
              {state.placeholder}
            </sdx-select-option>
          )}

          <slot />

          <div
            class="no-matches-found"
            style={{ display: state.noMatchesFound ? "flex" : "none" }}
          >
            <sdx-icon icon-name="icon-exclamation-mark" size={2} />
            {state.noMatchesFoundLabel}
          </div>
        </div>
      </Host>
    )
  }
}
